import { normaliseProject, type Project } from '@enercad/core';
import { t as $t } from '@enercad/core';

/*
 * Local projects: kept on the user's own computer, never sent to the server to be stored. A project is a
 * ".enercad" file the user picks (Chrome and Edge let a page read and write the file it was given); its last state is
 * also kept in this browser's own database, so a project opens even before the file is allowed again, and in
 * browsers that cannot write files (Firefox, Safari) the project lives there and is downloaded as a file.
 */

// the File System Access API (not in TypeScript's DOM types yet)
interface Handle {
  name: string;
  getFile(): Promise<File>;
  createWritable(): Promise<{ write(data: string): Promise<void>; close(): Promise<void> }>;
  queryPermission?(o: { mode: 'readwrite' }): Promise<PermissionState>;
  requestPermission?(o: { mode: 'readwrite' }): Promise<PermissionState>;
}
type Picker = (o: object) => Promise<Handle | Handle[]>;
const w = window as unknown as { showSaveFilePicker?: Picker; showOpenFilePicker?: Picker };

/** Can this browser write the project straight to a file on the computer? */
export const canWriteFiles = () => typeof w.showSaveFilePicker === 'function' && typeof w.showOpenFilePicker === 'function';

/** A named revision of a local project: kept inside its file, a full copy of the project as it was then. */
export interface LocalRevision {
  label: string;
  /** dd.mm.yyyy, as in the title block */
  date: string;
  note?: string;
  /** when it was made (ISO) */
  created: string;
  data: Project;
}

export interface LocalRecord {
  id: string;
  name: string;
  /** named revisions, oldest first */
  history?: LocalRevision[];
  /** the file, where the browser can write files */
  handle?: Handle;
  file?: string;
  data: Project;
  updated: string;
}

const TYPES = [{ description: 'EnerCAD', accept: { 'application/json': ['.enercad', '.json'] } }];
export const EXT = '.enercad';

function db(): Promise<IDBDatabase> {
  return new Promise((ok, fail) => {
    const r = indexedDB.open('enercad-local', 1);
    r.onupgradeneeded = () => r.result.createObjectStore('projects', { keyPath: 'id' });
    r.onsuccess = () => ok(r.result);
    r.onerror = () => fail(r.error ?? new Error($t('Tarayıcının yerel deposu açılamadı.')));
  });
}
async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const d = await db();
  return new Promise((ok, fail) => {
    const r = fn(d.transaction('projects', mode).objectStore('projects'));
    r.onsuccess = () => ok(r.result);
    r.onerror = () => fail(r.error);
  });
}

// the open file handles of this session (the stored ones are copies the browser gives back on the next visit)
const live = new Map<string, Handle>();
const handleOf = (r: LocalRecord) => live.get(r.id) ?? r.handle;

const newLocalId = () => 'l' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
// the file: the project, with its named revisions beside it (not part of the project itself, so they never travel
// to the server with it)
const text = (pr: Project, history?: LocalRevision[]) => JSON.stringify(history?.length ? { ...pr, history } : pr, null, 1);
function historyOf(raw: unknown): LocalRevision[] {
  const list = (raw as { history?: unknown })?.history;
  if (!Array.isArray(list)) return [];
  return list.slice(0, 200).flatMap((h) => {
    try {
      const { history: _, ...data } = h.data ?? {};
      return [{ label: String(h.label ?? '').slice(0, 20), date: String(h.date ?? '').slice(0, 20), ...(h.note ? { note: String(h.note).slice(0, 500) } : {}), created: String(h.created ?? ''), data: normaliseProject(data) }];
    } catch {
      return []; // a broken copy is left out, the rest stays
    }
  });
}

export const listLocal = async (): Promise<LocalRecord[]> => ((await tx('readonly', (s) => s.getAll())) as LocalRecord[]).sort((a, b) => b.updated.localeCompare(a.updated));
export const getLocal = (id: string) => tx('readonly', (s) => s.get(id)) as Promise<LocalRecord | undefined>;
const put = (r: LocalRecord) => tx('readwrite', (s) => s.put(r));
/** Takes the project off the list; its file stays where it is. */
export const forgetLocal = (id: string) => tx('readwrite', (s) => s.delete(id));

/** Is writing to the record's file allowed now (without asking)? */
export async function fileAllowed(r: LocalRecord): Promise<boolean> {
  return (await handleOf(r)?.queryPermission?.({ mode: 'readwrite' })) === 'granted';
}
/** Asks for the file again (call it from a click). */
export async function allowFile(r: LocalRecord): Promise<boolean> {
  const h = handleOf(r);
  if (!h) return false;
  return (await fileAllowed(r)) || (await h.requestPermission?.({ mode: 'readwrite' })) === 'granted';
}

async function writeFile(h: Handle, pr: Project, history?: LocalRevision[]) {
  const f = await h.createWritable();
  await f.write(text(pr, history));
  await f.close();
}

/**
 * A new local project: where the browser can, the user picks the file it is saved to (call from a click). The
 * project may come after the file is picked (one fetched from the server: the file dialog must open right on the
 * click). Null when the user cancels the file dialog.
 */
export async function createLocal(name: string, project: Project | (() => Promise<Project>), history?: LocalRevision[]): Promise<LocalRecord | null> {
  let handle: Handle | undefined;
  if (canWriteFiles()) {
    try {
      handle = (await w.showSaveFilePicker!({ suggestedName: (name || 'proje') + EXT, types: TYPES })) as Handle;
    } catch {
      return null;
    }
  }
  const pr = typeof project === 'function' ? await project() : project;
  if (handle) await writeFile(handle, pr, history);
  const r: LocalRecord = { id: newLocalId(), name: pr.name, ...(handle ? { handle, file: handle.name } : {}), ...(history?.length ? { history } : {}), data: pr, updated: new Date().toISOString() };
  await put(r);
  if (handle) live.set(r.id, handle);
  return r;
}

/** Opens a project file from the computer (call from a click); a file already on the list is opened as that one. */
export async function openLocalFile(file?: File): Promise<LocalRecord | null> {
  let handle: Handle | undefined;
  if (!file) {
    if (!canWriteFiles()) return null;
    try {
      [handle] = (await w.showOpenFilePicker!({ types: TYPES, multiple: false })) as Handle[];
    } catch {
      return null;
    }
    file = await handle.getFile();
  }
  const raw = JSON.parse(await file.text());
  const data = normaliseProject(raw), history = historyOf(raw);
  const list = await listLocal();
  // the same file again (the browser knows whether two handles point to one file)
  const same = handle && (await Promise.all(list.map(async (r) => ((await (handleOf(r) as any)?.isSameEntry?.(handle)) ? r : null)))).find(Boolean);
  const r: LocalRecord = { ...(same ?? { id: newLocalId() }), name: data.name, ...(handle ? { handle, file: handle.name } : {}), history, data, updated: new Date().toISOString() };
  await put(r);
  if (handle) live.set(r.id, handle);
  return r;
}

/**
 * The project as it is now: from its file where that is allowed (it may have been changed elsewhere; its named
 * revisions are taken from there too), else as last kept.
 */
export async function readLocal(r: LocalRecord): Promise<Project> {
  const h = handleOf(r);
  if (h && (await fileAllowed(r))) {
    try {
      const raw = JSON.parse(await (await h.getFile()).text());
      const history = historyOf(raw);
      if (JSON.stringify(history.map((x) => x.created)) !== JSON.stringify((r.history ?? []).map((x) => x.created))) await put({ ...r, history });
      return normaliseProject(raw);
    } catch {
      /* moved or unreadable: the last state kept here */
    }
  }
  return normaliseProject(r.data);
}

/** The named revisions of a local project, oldest first. */
export const localHistory = async (id: string): Promise<LocalRevision[]> => (await getLocal(id))?.history ?? [];

/** Adds a named revision (a copy of the project as it is) and writes the file with it. Says where it went. */
export async function addLocalRevision(id: string, rev: LocalRevision): Promise<'file' | 'browser' | 'needs-permission'> {
  const r = await getLocal(id);
  if (!r) throw new Error($t('Yerel proje bulunamadı.'));
  const history = [...(r.history ?? []).filter((x) => x.label !== rev.label), rev];
  await put({ ...r, history, data: rev.data, updated: new Date().toISOString() });
  const h = handleOf(r);
  if (!h) return 'browser';
  if (!(await fileAllowed(r))) return 'needs-permission';
  await writeFile(h, rev.data, history);
  return 'file';
}

/** Saves: always here, and to the file where that is allowed. Says where it went. */
export async function saveLocal(id: string, pr: Project): Promise<'file' | 'browser' | 'needs-permission'> {
  const r = await getLocal(id);
  if (!r) throw new Error($t('Yerel proje bulunamadı.'));
  await put({ ...r, name: pr.name, data: pr, updated: new Date().toISOString() });
  const h = handleOf(r);
  if (!h) return 'browser';
  if (!(await fileAllowed(r))) return 'needs-permission';
  await writeFile(h, pr, r.history);
  return 'file';
}
